import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useUserSettings } from 'src/Hooks'
import useModals from 'src/Hooks/useModals'
import { ChartSettings } from 'src/Libs/chart-kit'
import { UserSettings } from 'src/Libs/user/types'

export interface UseChartsFunctions {
  chartSettingsList: ChartSettings[]
  saveCharts: SaveCharts
  deleteChart: (chartId: number | string) => void
  handleClick: (serialNo: string) => void
}

export type SaveCharts = ({ chartId, chartSettings }: { chartId?: string | number; chartSettings: ChartSettings }) => void

interface Props {
  userSettings?: UserSettings
  userId?: number
}

/**
 * Custom hook for system charts functionality
 */
const useChartsFunctions = ({ userSettings, userId }: Props): UseChartsFunctions => {
  const { openSystemChartEditor } = useModals()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const dispatch = useDispatch()
  const { t } = useCustomTranslation()
  const { updateUserSettings } = useUserSettings()

  const chartSettingsList = useMemo<ChartSettings[]>(
    () =>
      userSettings?.chartConfig?.map(config => {
        try {
          return JSON.parse(config)
        } catch (err) {
          console.log(err)
          return {}
        }
      }) ?? [],
    [userSettings?.chartConfig]
  )

  const saveCharts = useCallback(
    ({ chartId, chartSettings }: { chartId?: string | number; chartSettings: ChartSettings }) => {
      updateUserSettings({
        chartConfig: [
          ...chartSettingsList.filter(chart => chart.id?.toString() !== chartId?.toString()).map(chart => JSON.stringify(chart)),
          JSON.stringify({ ...chartSettings, id: chartId }),
        ],
      })
        .catch(err => enqueueErrorStack(`${t('systemDataHandler.editor.saveChartFailed')}: ${err}`))
        .then(() => {
          enqueueSuccessStack(`${t('systemDataHandler.editor.saveChartSucceded')}`)
          dispatch(refreshApiView())
        })
    },
    [chartSettingsList, dispatch, enqueueErrorStack, enqueueSuccessStack, t, updateUserSettings]
  )

  const deleteChart = useCallback(
    (chartId: string | number) => {
      updateUserSettings({
        chartConfig: [...chartSettingsList.filter(chart => chart.id?.toString() !== chartId?.toString()).map(chart => JSON.stringify(chart))],
      })
        .then(() => {
          enqueueSuccessStack(`${t('systemDataHandler.editor.deleteChartSucceded')}`)
          dispatch(refreshApiView())
        })
        .catch(err => enqueueErrorStack(`${t('systemDataHandler.editor.deleteChartFailed')}: ${err}`))
    },
    [chartSettingsList, dispatch, enqueueErrorStack, enqueueSuccessStack, t, updateUserSettings]
  )

  const handleEditClick = useCallback(
    (serialno: string): void => {
      openSystemChartEditor({
        handleSaveChart: props => saveCharts({ chartId: props.id, chartSettings: props }),
        serialNumber: serialno,
        views: { hour: 'bar', day: 'line', week: 'line', month: 'bar', year: 'bar' },
      })
    },
    [openSystemChartEditor, saveCharts]
  )

  const systemChartProps = useMemo(() => {
    return { saveCharts, deleteChart, chartSettingsList, handleClick: handleEditClick }
  }, [saveCharts, deleteChart, chartSettingsList, handleEditClick])

  return systemChartProps
}

export default useChartsFunctions
